/**
 * «Rehacer la hoja» de un plano: lo que se pierde al reemplazarla, en frases para el aviso. Solo cuenta;
 * los números los da el servidor (GET .../planos/:fileId/hoja/trabajo-en-riesgo).
 *
 * Es el equivalente, para la hoja de un plano, de `trabajoQueSePierdeAlRearmar` (la HP de un concurso):
 * el aviso aparece solo si hay algo que perder; si la hoja no tiene nada hecho a mano, solo se dice cuántos
 * renglones se reemplazan.
 */
import type { TrabajoEnRiesgoPlano } from '@supernova/shared';

const plural = (n: number, uno: string, varios: string) => (n === 1 ? `1 ${uno}` : `${n} ${varios}`);

/** Lo que se pierde de verdad (más allá de los renglones mismos, que el asistente vuelve a armar). */
export function lineasQueSePierden(t: TrabajoEnRiesgoPlano): string[] {
  const out: string[] = [];
  const analisis = t.analisisAceptados + t.analisisSinAceptar + t.analisisRechazados;
  if (analisis > 0) out.push(`${plural(analisis, 'análisis generado con IA', 'análisis generados con IA')} (habría que volver a generarlos)`);
  if (t.renglonesCorregidos > 0) out.push(plural(t.renglonesCorregidos, 'renglón que corregiste a mano', 'renglones que corregiste a mano'));
  if (t.conceptosNoSeleccionados > 0) out.push(plural(t.conceptosNoSeleccionados, 'concepto que quitaste del presupuesto', 'conceptos que quitaste del presupuesto'));
  if (t.opcionesGuardadas > 0) out.push(plural(t.opcionesGuardadas, 'opción guardada', 'opciones guardadas'));
  if (t.procedimientos > 0) out.push(plural(t.procedimientos, 'procedimiento constructivo', 'procedimientos constructivos'));
  if (t.notasQuitadas > 0) out.push(`${plural(t.notasQuitadas, 'renglón', 'renglones')} con notas que quitaste a mano (vuelven a pegarse)`);
  if (t.conAvance > 0) out.push(`${plural(t.conAvance, 'renglón con avance de obra', 'renglones con avance de obra')} (su avance queda «por revisar» hasta que se vuelva a enviar desde OPUS 27)`);
  if (t.enCola > 0) out.push(`${plural(t.enCola, 'análisis en cola', 'análisis en cola')} para generarse con IA (no se generan)`);
  return out;
}

/** Hay análisis generándose: no se puede reemplazar hasta que terminen (el servidor también lo frena). */
export function bloqueaRehacer(t: TrabajoEnRiesgoPlano): string | null {
  if (t.generandose <= 0) return null;
  return `${t.generandose === 1 ? 'Un análisis se está' : `${t.generandose} análisis se están`} generando con IA en esta hoja. Espera a que terminen para rehacerla.`;
}
